import {Pipe, PipeTransform} from '@angular/core';
import {I18NService} from '../i18n/i18n.service';
import {
    EC_DOCUMENT_CI, ECEntitySubtype,
    ECJuridicalType,
    ECLegalForm,
    ECPodUtilityType,
    ECSubjectType
} from 'smag-commons/cfg/hierarchy';
import {isDefined} from '../commons/commons';
import {DEFAULT_DECIMAL_SEPARATOR} from '../i18n/i18n-defaults';

@Pipe({name: 'genericPipe'})
export class GenericPipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    transform(value: string | ECPodUtilityType): string {
        return this.i18n.translate((value.charAt(0).toUpperCase() + value.slice(1)).replace(/_/g, ' '));
    }
}

@Pipe({
    name: 'niceValue'
})
export class NiceValuePipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    private static EPSILON = 1e-10;

    private static fixMultiple(val: number, uom: string): [number, string] {
        if (!uom) {
            return [val, uom];
        }
        const aval = Math.abs(val);
        const fixUom = uom.substring(1) ? uom.substring(1) : uom;
        if (aval >= 1000000000000) {
            if (uom === 'W' || uom === 'Wh' || uom === 'VAR' || uom === 'VARh') {
                return [val / 1000000000000, `T${fixUom}`];
            } else if (uom === 'kW' || uom === 'kWh' || uom === 'kVAR' || uom === 'kVARh') {
                return [val / 1000000000000, `P${fixUom}`];
            } else if (uom === 'MW' || uom === 'MWh' || uom === 'MVAR' || uom === 'MVARh') {
                return [val / 1000000000000, `E${fixUom}`];
            }
        }
        if (aval >= 1000000000) {
            if (uom === 'W' || uom === 'Wh' || uom === 'VAR' || uom === 'VARh') {
                return [val / 1000000000, `G${fixUom}`];
            } else if (uom === 'kW' || uom === 'kWh' || uom === 'kVAR' || uom === 'kVARh') {
                return [val / 1000000000, `T${fixUom}`];
            } else if (uom === 'MW' || uom === 'MWh' || uom === 'MVAR' || uom === 'MVARh') {
                return [val / 1000000000, `P${fixUom}`];
            }
        }
        if (aval >= 1000000) {
            if (uom === 'W' || uom === 'Wh' || uom === 'VAR' || uom === 'VARh') {
                return [val / 1000000, `M${fixUom}`];
            } else if (uom === 'kW' || uom === 'kWh' || uom === 'kVAR' || uom === 'kVARh') {
                return [val / 1000000, `G${fixUom}`];
            } else if (uom === 'MW' || uom === 'MWh' || uom === 'MVAR' || uom === 'MVARh') {
                return [val / 1000000, `T${fixUom}`];
            }
        }
        if (aval >= 1000) {
            if (uom === 'W' || uom === 'Wh' || uom === 'VAR' || uom === 'VARh') {
                return [val / 1000, `k${fixUom}`];
            } else if (uom === 'kW' || uom === 'kWh' || uom === 'kVAR' || uom === 'kVARh') {
                return [val / 1000, `M${fixUom}`];
            } else if (uom === 'MW' || uom === 'MWh' || uom === 'MVAR' || uom === 'MVARh') {
                return [val / 1000, `G${fixUom}`];
            } else if (uom === 'GW' || uom === 'GWh' || uom === 'GVAR' || uom === 'GVARh') {
                return [val / 1000, `T${fixUom}`];
            } else if (uom === 'V') {
                return [val / 1000, 'kV'];
            }
        }
        return [val, uom];
    }

    transform(value: number, uom?: string, decimals?: number): string {
        let val: string;
        let u = uom;
        if (!isDefined(value)) {
            return '';
        }
        if (Math.abs(value) < NiceValuePipe.EPSILON) {
            value = 0;
        }
        if (isDefined(decimals)) {
            val = value.toFixed(decimals);
        } else {
            const [v, uu] = NiceValuePipe.fixMultiple(value, uom);
            u = uu;
            const aval = Math.abs(v);
            if (aval % 1 === 0) { // integer, no mantissa
                val = v.toFixed(0);
            } else {
                const decimalPlaces = aval.toString().split('.')[1]?.length || 0;
                if (decimalPlaces === 1 || decimalPlaces === 2) {
                    val = v.toFixed(decimalPlaces);
                } else {
                    val = v.toFixed(2);
                }
            }
        }
        if (parseFloat(val) === 0) {
            val = '0';
        }
        if (u) {
            const space = u === '%' ? '' : ' ';
            val = val + space + u;
        }
        return val.replace('.', this.i18n.decimalSeparator(DEFAULT_DECIMAL_SEPARATOR));
    }
}

@Pipe({name: 'ecDocumentType'})
export class ECDocumentTypePipe implements PipeTransform {
    transform(type: string): string {
        switch (type) {
            case EC_DOCUMENT_CI:
                return 'Identity document';
            default:
                return type;
        }
    }
}

@Pipe({name: 'ecDocumentTypeI18n'})
export class ECDocumentTypeI18nPipe implements PipeTransform {

    constructor(private readonly i18n: I18NService) {
    }

    transform(type: string): string {
        switch (type) {
            case EC_DOCUMENT_CI:
                return this.i18n.translate('Identity document');
            default:
                return type;
        }
    }
}

@Pipe({name: 'ecMemberJuridicalType'})
export class ECMemberJuridicalTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECJuridicalType): string {
        switch (type) {
            case ECJuridicalType.CORPORATE_ENTITY:
                return 'Corporate entity';
            case ECJuridicalType.INDIVIDUAL:
                return 'Individual';
            default:
                return type;
        }
    }
}

@Pipe({name: 'ecMemberLegalFormType'})
export class ECMemberLegalFormTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECLegalForm): string {
        switch (type) {
            case ECLegalForm.OTHER_INDIVIDUALS:
                return 'Other individuals';
            case ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE:
                return 'Entrepreneur, sole proprietorship, agricultural enterprise';
            default:
                return type;
        }
    }
}

@Pipe({name: 'ecMemberSubjectType'})
export class ECMemberSubjectTypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECSubjectType): string {
        switch (type) {
            case ECSubjectType.PMI:
                return 'SME';
            case ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY:
                return 'Territorial body, local authority, other body';
            case ECSubjectType.OTHER:
                return 'Other';
            case ECSubjectType.ASSOCIATION_WITH_LEGAL_PERSONALITY_UNDER_PRIVATE_LAW:
                return 'Association with legal personality under private law';
            case ECSubjectType.ASSOCIATED_FIRM_COMPANY_OF_PROFESSIONALS:
                return 'Associated firm company of professionals';
            case ECSubjectType.COMMUNITY:
                return 'Community';
            case ECSubjectType.CONDOMINIUM:
                return 'Condominium';
            default:
                return type;
        }
    }
}

@Pipe({name: 'ecMemberEntitySubtype'})
export class ECMemberEntitySubtypePipe implements PipeTransform {

    constructor() {
    }

    transform(type: ECEntitySubtype): string {
        switch (type) {
            case ECEntitySubtype.ENVIRONMENT_PREVENTION_ORGANIZATION:
                return 'Environment prevention organization';
            case ECEntitySubtype.STATE:
                return 'State';
            case ECEntitySubtype.REGION:
                return 'Region';
            case ECEntitySubtype.ISLAND_COMMUNITY:
                return 'Island community';
            case ECEntitySubtype.PROVINCE:
                return 'Province';
            case ECEntitySubtype.LOCAL_ADMINISTRATION_ORGANIZATION:
                return 'Local administration organization';
            case ECEntitySubtype.MOUNTAIN_COMMUNITY:
                return 'Mountain community';
            case ECEntitySubtype.METROPOLITAN_CITY:
                return 'Metropolitan city';
            case ECEntitySubtype.RELIGIOUS_ORGANIZATION:
                return 'Religious organization';
            case ECEntitySubtype.RESEARCH_AND_TRAINING_ORGANIZATION:
                return 'Research and training organization';
            case ECEntitySubtype.MUNICIPALITY:
                return 'Municipality';
            case ECEntitySubtype.MUNICIPALITIES_UNION:
                return 'Municipalities union';
            case ECEntitySubtype.THIRD_SECTOR_ORGANIZATION:
                return 'Third sector organization';
            default:
                return type;
        }
    }
}
